import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'

import { fetchWhat } from 'utils/api'

import { useAppDispatch, useAppSelector } from 'store'
import $app from 'store/app'
import $business from 'store/business'
import $customer, { CustomerBookingField, CustomerType } from 'store/customer'
import $category from 'store/category'
import $service from 'store/service'
import $resource from 'store/resource'
import $day from 'store/day'
import $order from 'store/order'
import $wlist from 'store/wlist'

import App from 'components/App'
import Dialog from 'components/Dialog'
import HomeHero from 'components/Home/HomeHero'
import HomeHeader from 'components/Home/HomeHeader'
import HomeData from 'components/Home/HomeData'
import HomeContactUs from 'components/Home/HomeContactUs'
import AppointmentDialog from 'components/Appointment/AppointmentDialog'
import HelperBookingFields from 'components/Helper/HelperBookingFields/HelperBookingFields'

import styles from './HomeContent.module.scss'

const HomeContent: React.FC = () => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()
  const reset = useAppSelector(({ $app }) => $app.reset)
  const showContent = useAppSelector(({ $app }) => $app.showContent)
  const settings = useAppSelector($business.get.settings)
  const about = useAppSelector($business.get.about)
  const aiAgent = useAppSelector($business.get.aiAgent)
  const customer = useAppSelector(({ $customer }) => $customer.data)
  const temp = useAppSelector(({ $customer }) => $customer.temp)
  const phone = useAppSelector($customer.get.phone)
  const customerId = useAppSelector($customer.get.customerId)
  const customers = useAppSelector(({ $customer }) => $customer.data.customers)
  const unfilledFields = useAppSelector($customer.get.unfilledFields)
  const editId = useAppSelector(({ $order }) => $order.editId)
  const whitelist = useAppSelector(({ $app }) => $app.whitelist)
  const wlistId = useAppSelector(({ $wlist }) => $wlist.id)

  const [error, setError] = useState({ phone: '', name: '' })
  const [bookingFieldValues, setBookingFieldValues] = useState<Record<string, any>>({})
  const [bookingFieldErrors, setBookingFieldErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [scrollBottom, setScrollBottom] = useState(false)
  const [dialogVisible, setDialogVisible] = useState({ confirm: false, notAllowBooking: false, appointment: false, popupText: false, aiAgentPopup: false, requiredFields: false })

  const contentRef = useRef<HTMLDivElement>(null)
  const phoneRef = useRef<HTMLInputElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (reset) {
      dispatch($order.set.fetch(true))
      dispatch($wlist.set.fetch(true))

      setTimeout(() => {
        dispatch($category.set.reset())
        dispatch($service.set.reset())
        dispatch($resource.set.reset())
        dispatch($day.set.reset())
        dispatch($order.set.reset())
        dispatch($wlist.set.reset())
        dispatch($app.set.reset(false))
      }, 300)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset])

  useEffect(() => {
    if (showContent && about.popup_text) {
      setDialogVisible(prev => ({ ...prev, popupText: true }))
    }
  }, [showContent, about.popup_text])

  useEffect(() => {
    if (showContent && aiAgent.show_ai_agent_popup) {
      setDialogVisible(prev => ({ ...prev, aiAgentPopup: true }))
    }
  }, [showContent, aiAgent.show_ai_agent_popup])

  useEffect(() => {
    if (showContent) {
      const customerPhone = localStorage.getItem('customerPhone')
      const savedCustomerId = localStorage.getItem('customerId')
      
      if (customerPhone) {
        localStorage.removeItem('customerTemp')

        dispatch($customer.set.loggedIn(true))
        dispatch($customer.set.bookingFieldValues({}))
        setBookingFieldValues({})
        setBookingFieldErrors({})
        if (savedCustomerId) {
          dispatch($customer.set.selectedCustomerId(Number(savedCustomerId)))
        }
        fetchCustomer(customerPhone, true)
      }

      const customerTemp = localStorage.getItem('customerTemp')
      if (customerTemp) {
        const temp = JSON.parse(customerTemp)
        dispatch($customer.set.loggedIn(true))
        dispatch($customer.set.temp({ phone: temp.phone || '', name: temp.name || '' }))

        fetchUnfilledFieldsForTemp()

        const isWhitelisted = whitelist.find(item => item === temp.phone)
        dispatch($customer.set.confirmed(!!isWhitelisted))

        if (temp.bookingFieldValues) {
          dispatch($customer.set.bookingFieldValues(temp.bookingFieldValues))
          setBookingFieldValues(temp.bookingFieldValues)
        }
      }

      if (contentRef.current) {
        processShadow(contentRef.current)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showContent])

  useEffect(() => {
    if (customer.loaded) {
      if (!customer.loggedIn && !customer.allow_book) {
        phoneRef.current?.blur()
        handleDialogOpen('notAllowBooking')()
      }

      const isWhitelisted = whitelist.find(item => item === phone)
      dispatch($customer.set.confirmed(!!isWhitelisted))

      if (!customer.loggedIn && customer?.id) {
        if (!isWhitelisted && !settings.disable_confirmation_code) {
          handleDialogOpen('confirm')()
        } else {
          handleCodeSuccess()
        }
      }

      if (wlistId) {
        prepareForWlistBooking()
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.loaded])

  const prepareForWlistBooking = async () => {
    await fetchWhat(dispatch, phone)

    dispatch($order.set.step(2))
    handleDialogOpen('appointment')()
  }

  const fetchUnfilledFieldsForTemp = async () => {
    const result = await $customer.api.getUnfilledFields()
    if (result && result.status === 'success') {
      dispatch($customer.set.unfilled_fields(result.unfilled_fields || []))
    }
  }

  const fetchCustomer = async (customerPhone: string, loggedIn: boolean = false) => {
    setLoading(true)
    
    const savedCustomerId = localStorage.getItem('customerId')
    const result = await $customer.api.get({ number: customerPhone })
    if (result && result.status === 'success') {
      const { allow_book, customer, status, customers, unfilled_fields } = result
      dispatch($customer.set.data({ allow_book, customer, status, customers }))
      dispatch($customer.set.unfilled_fields(unfilled_fields))

      if (customer && customer.id) {
        dispatch($customer.set.selectedCustomerId(customer.id))
        localStorage.setItem('customerId', String(customer.id))
      } else if (!customer && customers && customers.length > 0 && savedCustomerId) {
        const savedId = Number(savedCustomerId)
        const foundCustomer = customers.find((c: CustomerType) => c.id === savedId)
        if (foundCustomer) {
          const selectedId = foundCustomer.id || null
          dispatch($customer.set.selectedCustomerId(selectedId))
          if (selectedId) {
            localStorage.setItem('customerId', String(selectedId))
          }
          dispatch($customer.set.data({
            allow_book,
            customer: foundCustomer,
            status,
            customers,
          }))
          dispatch($customer.set.temp({
            ...temp,
            name: foundCustomer.firstname || temp.name,
          }))

          setLoading(false)
          return
        }
      }

      if (!customer) {
        if (!wlistId) {
          if (loggedIn) {
            dispatch($customer.set.loaded(false))
          }
          dispatch($customer.set.loggedIn(false))
        } else {
          localStorage.removeItem('customerPhone')
          localStorage.removeItem('customerId')
          localStorage.setItem('customerTemp', JSON.stringify({
            ...temp,
            bookingFieldValues: bookingFieldValues
          }))

          const isWhitelisted = whitelist.find(item => item === temp.phone)
          dispatch($customer.set.confirmed(!!isWhitelisted))
        }
      }
    } else {
      dispatch($app.set.error({ message: t(result?.message) }))
    }

    setLoading(false)
  }

  const openAgentWhatsApp = () => {
    if (!about.whatsapp) return
    let normalizedNumber = about.whatsapp.replace(/-|\s/g, '')
    if (normalizedNumber.startsWith('+972')) {
      normalizedNumber = normalizedNumber.substring(1)
    } else if (normalizedNumber.startsWith('0')) {
      normalizedNumber = '972' + normalizedNumber.substring(1)
    }
    const message = encodeURIComponent('היי, אני רוצה לקבוע תור!')
    window.open(`https://wa.me/${normalizedNumber}?text=${message}`, '_blank', 'noopener,noreferrer')
  }

  const handleDialogOpen = (key: string) => () => {
    setDialogVisible(prev => ({ ...prev, [key]: true }))
  }

  const handleDialogClose = (key: string) => () => {
    setDialogVisible(prev => ({ ...prev, [key]: false }))

    if (key === 'appointment') {
      dispatch($order.set.editId(null))
    }
  }

  const handleFormChange = (field: string) => async (value: string) => {
    setError({ phone: '', name: '' })

    const formatedValue = field === 'phone' ? removeSpecialSymbols(value) : value
    dispatch($customer.set.temp({ ...temp, [field]: formatedValue }))

    if (field === 'phone') {
      const formatedPhone = isValidNumber(formatedValue)
      if (formatedPhone) {
        dispatch($customer.set.selectedCustomerId(null))
        if (!customer.loaded) {
          await fetchCustomer(formatedPhone)
        }
      } else {
        dispatch($customer.set.loaded(false))
      }
    }
  }

  const removeSpecialSymbols = (inputValue: string) => {
    let value = inputValue.replace(/\D/g, '')
    return value
  }

  const isValidNumber = (value: string) => {
    return value.length >= 10 ? value : false
  }

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    processShadow(event.currentTarget)
  }

  const processShadow = (div: HTMLDivElement) => {
    const { scrollTop, clientHeight, scrollHeight } = div

    if (scrollTop + clientHeight + 1 >= scrollHeight) {
      setScrollBottom(true)
    } else {
      if (scrollBottom === true) {
        setScrollBottom(false)
      }
    }
  }

  const handleAppointment = () => {
    const customerTempData = localStorage.getItem('customerTemp')
    const shouldCheckUnfilledFields = customer?.id || (customerTempData && customer.loggedIn)

    if (shouldCheckUnfilledFields) {
      const hasRequiredUnfilledFields = checkRequiredUnfilledFields()
      if (hasRequiredUnfilledFields) {
        handleDialogOpen('requiredFields')()
        return
      }
      
      if (customer.loggedIn) {
        handleDialogOpen('appointment')()
        return
      }
    }

    if (!customer.loggedIn) {
      const nextError = { phone: '', name: '' }
      let hasError = false

      if (phone === '') {
        nextError.phone = t('Please enter a valid phone number')
        hasError = true
      }

      if (!customer?.id && temp.name.trim() === '') {
        nextError.name = t('Please enter your name')
        hasError = true
      }

      setError(nextError)

      const requiredFields = (unfilledFields ?? []).filter((field: CustomerBookingField) => field.required)
      const nextErrors: Record<string, string> = {}

      if (requiredFields.length > 0) {
        requiredFields.forEach((field: CustomerBookingField) => {
          const value = bookingFieldValues[field.name]
          const isEmptyArray = Array.isArray(value) && value.length === 0
          const isEmptyString = typeof value === 'string' && value.trim() === ''
          const isEmptyValue = value == null || value === '' || isEmptyString || isEmptyArray

          if (isEmptyValue) {
            nextErrors[field.name] = t('This field is required')
          }
        })
      }

      setBookingFieldErrors(nextErrors)

      if (Object.keys(nextErrors).length > 0) {
        hasError = true
      }

      if (hasError) {
        if (nextError.phone) {
          phoneRef.current?.scrollIntoView({ behavior: 'smooth' })
        } else if (nextError.name) {
          nameRef.current?.scrollIntoView({ behavior: 'smooth' })
        }

        return
      }
    }

    handleDialogOpen('appointment')()
  }

  const checkRequiredUnfilledFields = (): boolean => {
    if (customer?.id) {
      return (unfilledFields ?? []).length > 0
    }

    const customerTempData = localStorage.getItem('customerTemp')
    if (customerTempData) {
      try {
        const tempData = JSON.parse(customerTempData)
        const savedBookingFields = tempData.bookingFieldValues || {}

        const actuallyUnfilledFields = (unfilledFields ?? []).filter((field: CustomerBookingField) => {
          const value = savedBookingFields[field.name]
          const isEmptyArray = Array.isArray(value) && value.length === 0
          const isEmptyString = typeof value === 'string' && value.trim() === ''
          const isEmptyValue = value == null || value === '' || isEmptyString || isEmptyArray
          
          return isEmptyValue
        })

        return actuallyUnfilledFields.length > 0
      } catch (error) {
        console.error('Error parsing customerTemp:', error)
        return false
      }
    }

    return false
  }

  const getUnfilledFieldsForDialog = (): CustomerBookingField[] => {
    if (customer?.id) {
      return unfilledFields ?? []
    }

    const customerTempData = localStorage.getItem('customerTemp')
    if (customerTempData) {
      try {
        const tempData = JSON.parse(customerTempData)
        const savedBookingFields = tempData.bookingFieldValues || {}

        return (unfilledFields ?? []).filter((field: CustomerBookingField) => {
          const value = savedBookingFields[field.name]
          const isEmptyArray = Array.isArray(value) && value.length === 0
          const isEmptyString = typeof value === 'string' && value.trim() === ''
          const isEmptyValue = value == null || value === '' || isEmptyString || isEmptyArray
          
          return isEmptyValue
        })
      } catch (error) {
        console.error('Error parsing customerTemp:', error)
        return unfilledFields ?? []
      }
    }

    return unfilledFields ?? []
  }

  const handleRequiredFieldsComplete = (values: Record<string, any>) => {
    const mergedValues = { ...bookingFieldValues, ...values }
    setBookingFieldValues(mergedValues)
    dispatch($customer.set.bookingFieldValues(mergedValues))
    
    setDialogVisible(prev => ({
      ...prev,
      requiredFields: false,
      appointment: true,
    }))
  }

  const handleBookingFieldChange = (fieldName: string, value: any) => {
    const nextValues = { ...bookingFieldValues, [fieldName]: value }
    setBookingFieldValues(nextValues)
    dispatch($customer.set.bookingFieldValues(nextValues))

    let customerTempData = localStorage.getItem('customerTemp')
    if (customerTempData) {
      const tempData = JSON.parse(customerTempData)
      tempData.bookingFieldValues = nextValues
      localStorage.setItem('customerTemp', JSON.stringify(tempData))
    } else {
      const newTempData = {
        phone: temp.phone,
        name: temp.name,
        bookingFieldValues: nextValues
      }
      localStorage.setItem('customerTemp', JSON.stringify(newTempData))
    }

    if (bookingFieldErrors[fieldName]) {
      setBookingFieldErrors(prev => ({ ...prev, [fieldName]: '' }))
    }
  }

  const handleCodeSuccess = () => {
    localStorage.setItem('customerPhone', phone)
    if (customerId) {
      localStorage.setItem('customerId', String(customerId))
    }
    dispatch($customer.set.loggedIn(true))

    dispatch($customer.set.bookingFieldValues({}))
    setBookingFieldValues({})
    setBookingFieldErrors({})
  }

  const handleCustomerSelect = (selectedCustomer: CustomerType) => {
    const selectedId = selectedCustomer.id || null
    dispatch($customer.set.selectedCustomerId(selectedId))
    dispatch($customer.set.data({
      allow_book: customer.allow_book,
      customer: selectedCustomer,
      status: 'success',
      customers: customer.customers || [],
    }))
    dispatch($customer.set.temp({
      ...temp,
      name: selectedCustomer.firstname || temp.name,
    }))
  }

  const handleLogout = () => {
    dispatch($customer.set.logout())
    dispatch($order.set.fetch(true))
    dispatch($wlist.set.fetch(true))
  }

  const isRtl = useAppSelector(({ $app }) => $app.isRtl)

  return (
    <App.Flex column className={styles.container}>
      <div ref={contentRef} className={styles.content} onScroll={handleScroll}>
        <App.Flex column className={styles.scroll}>
          <App.Flex column jEnd className={styles.top}>
            <HomeHero />
          </App.Flex>

          <App.Flex column fullWidth gap={8} className={styles.scrollContent}>
            <App.Flex className={styles.topDiv} />

            <App.Flex column gap={4}>
              {customer.loggedIn ? (
                <App.Flex center className={styles.logout} onClick={handleLogout}>
                  <App.Icon icon="sign-out" small rotate={isRtl} />
                </App.Flex>
              ) : null}
              <HomeHeader />

              {customer.loggedIn ? (
                <App.Flex row center gap={4}>
                  {!customer.confirmed ? (
                    <App.Button flex={1} onClick={handleDialogOpen('confirm')}>{t('My reservations')}</App.Button>
                  ) : null}
                </App.Flex>
              ) : null}
            </App.Flex>

            {settings.loaded && about.whatsapp && settings.ai_agent_enabled && (settings.agent_trigger === 'both' || settings.agent_trigger === 'online_booking') ? (
              <App.Flex row center fullWidth gap={4} className={styles.agentButtonWrap}>
                <App.Button flex={1} onClick={openAgentWhatsApp}>
                  {aiAgent.ai_button_text || t('Click here - Let the secretary schedule an appointment for you')}
                </App.Button>
              </App.Flex>
            ) : null}

            {customer.loggedIn ? (
              customer.confirmed ? (
                <HomeData />
              ) : null
            ) : (
              <App.Flex column>
                <App.Flex column gap={4}>
                  <App.Text bold height={1.2}>{t('Phone number')}:</App.Text>
                  <App.Input
                    ref={phoneRef}
                    type="tel"
                    placeholder={t('Enter your phone number')}
                    value={temp.phone}
                    error={error.phone}
                    loading={loading}
                    limit={12}
                    onChange={handleFormChange('phone')}
                  />
                </App.Flex>

                {customer.loaded && !customer?.id && customers && customers.length > 0 ? (
                  <App.Flex column gap={4} className={cn(styles.nameBox, styles.open)} style={{ height: customers.length * (48 + 16) }}>
                    <App.Flex column fullWidth className={styles.nameBoxInner}>
                      <App.Flex column gap={2} fullWidth>
                        {customers.map((customerItem: CustomerType) => (
                          <App.Button
                            key={customerItem.id}
                            outlined
                            fullWidth
                            onClick={() => handleCustomerSelect(customerItem)}
                          >
                            {customerItem.firstname || customerItem.lastname ? 
                              `${customerItem.firstname || ''} ${customerItem.lastname || ''}`.trim() : 
                              t('Customer')
                            }
                          </App.Button>
                        ))}
                      </App.Flex>
                    </App.Flex>
                  </App.Flex>
                ) : (
                  <App.Flex column gap={4} className={cn(styles.nameBox, {[styles.open]: customer.loaded && !customer?.id && customer.allow_book})}>
                    <App.Flex column fullWidth gap={4} className={styles.nameBoxInner}>
                      <App.Input
                        ref={nameRef}
                        placeholder={t('Enter your name')}
                        value={temp.name}
                        error={error.name}
                        onChange={handleFormChange('name')}
                      />

                      <HelperBookingFields
                        values={bookingFieldValues}
                        errors={bookingFieldErrors}
                        onFieldChange={handleBookingFieldChange}
                      />
                    </App.Flex>
                  </App.Flex>
                )}

                <App.Flex column gap={4} className={cn(styles.codeBox, {[styles.open]: customer.loaded && customer?.id})}>
                  <App.Flex column fullWidth className={styles.codeBoxInner}>
                    <App.Button outlined onClick={handleDialogOpen('confirm')}>{t('Enter a code')}</App.Button>
                  </App.Flex>
                </App.Flex>
              </App.Flex>
            )}

            <HomeContactUs />

            <App.Flex center gap={1}>
              <App.Text xs light gray>&copy; {t('Powered by')}</App.Text>
              <App.Text tag="a" href="https://tiffulit.co.il" target="_blank" rel="noreferrer" xs semiBold highlight>{t('TIFFULIT')}</App.Text>
            </App.Flex>
          </App.Flex>
        </App.Flex>
      </div>

      <App.Flex center padding={6} className={cn(styles.button, {[styles.bottom]: scrollBottom})}>
        <App.Button fullWidth disabled={customer.loaded && !customer.allow_book} onClick={handleAppointment}>{t('Make Appointment')}</App.Button>
      </App.Flex>

      <AppointmentDialog open={dialogVisible.appointment || editId != null} onClose={handleDialogClose('appointment')} />
      <Dialog.CodeConfirm open={dialogVisible.confirm} onSuccess={handleCodeSuccess} onClose={handleDialogClose('confirm')} />
      <Dialog.NotAllowBooking open={dialogVisible.notAllowBooking} onClose={handleDialogClose('notAllowBooking')} />
      <Dialog.AiAgentPopup
        open={dialogVisible.aiAgentPopup}
        text={aiAgent.ai_button_text}
        onClose={handleDialogClose('aiAgentPopup')}
        onYes={() => {
          openAgentWhatsApp()
          handleDialogClose('aiAgentPopup')()
        }}
      />
      <Dialog.PopupText open={dialogVisible.popupText} text={about.popup_text} onClose={handleDialogClose('popupText')} />
      <Dialog.RequiredFields 
        open={dialogVisible.requiredFields} 
        onClose={handleDialogClose('requiredFields')} 
        onComplete={handleRequiredFieldsComplete}
        unfilledFields={getUnfilledFieldsForDialog()}
        values={bookingFieldValues}
      />
      <Dialog.CancelOrderConfirm />
      <Dialog.CancelWlistConfirm />
      <Dialog.WlistCalendar />
      <Dialog.WlistTime />
      <Dialog.Error />
    </App.Flex>
  )
}

export default HomeContent